import {
  syncOberonAll,
  syncOberonDelta,
  getOberonSyncStatus,
  cancelOberonSyncJob,
} from "app/services/api/integrations";

/**
 * Start OBERON sync and return jobId for polling
 * @param {string} customerCode - The customer code
 * @param {Function} syncFunction - The sync function to call (syncOberonAll or syncOberonDelta)
 * @returns {Promise<{jobId: string|null, result: any}>}
 */
export const startOberonSync = async (customerCode, syncFunction = syncOberonAll) => {
  const result = await syncFunction(customerCode);
  // Tolerant parsing: jobId can be at result.jobId or result.data.jobId
  const jobId = result?.jobId || result?.data?.jobId || null;
  return { jobId, result };
};

/**
 * Fetch OBERON sync status with tolerant parsing
 *
 * Expected response structure:
 * {
 *   "jobId": "...",
 *   "status": "done" | "running" | "error" | ...,
 *   "step": "sync-product-images",
 *   "progress": {
 *     "message": "All steps completed successfully",
 *     ...
 *   }
 * }
 */
export const fetchOberonSyncStatus = async (jobId) => {
  const result = await getOberonSyncStatus(jobId);

  // Status is at root level or nested in data
  const status =
    result?.status ||
    result?.data?.status ||
    result?.progress?.status ||
    result?.data?.progress?.status ||
    "unknown";

  // Progress object for message
  const progress =
    result?.progress || result?.data?.progress || result?.data || {};

  // Message from progress.message or progress.msg
  const message = progress?.message || progress?.msg || "";

  // Current step name
  const step = result?.step || result?.data?.step || "";

  // Optional percent
  const percent = progress?.percent ?? null;

  // CreatedAt timestamp for timeout check
  const createdAt = result?.createdAt || result?.data?.createdAt || null;

  // Import summary (counts) shown in a modal when the job completes.
  const summary = progress?.summary || null;

  return {
    status,
    message,
    step,
    percent,
    createdAt,
    summary,
    raw: result,
  };
};

/**
 * Cancel OBERON sync job
 */
export const cancelOberonJob = async (jobId) => {
  await cancelOberonSyncJob(jobId);
};

/**
 * Check if status is terminal (job completed)
 */
export const isTerminalStatus = (status) => {
  const terminalStates = ["success", "done", "completed", "error", "failed"];
  return terminalStates.includes(status?.toLowerCase());
};

/**
 * Check if status indicates success
 */
export const isSuccessStatus = (status) => {
  const successStates = ["success", "done", "completed"];
  return successStates.includes(status?.toLowerCase());
};

/**
 * Shared handler for OBERON import (full or delta)
 * @param {Object} params - Handler parameters
 * @param {Object} params.customer - Customer object
 * @param {Function} params.setImportOpen - Function to set import modal open state
 * @param {Function} params.setImportProgress - Function to set import progress
 * @param {Function} params.setOberonSyncJobId - Function to set sync job ID
 * @param {Function} params.setOberonSyncMessage - Function to set sync message
 * @param {Function} params.setOberonSyncStartTime - Function to set sync start time
 * @param {Function} params.setIsOberonSyncRunning - Function to set sync running state
 * @param {Function} params.enqueueSnackbar - Function to show notifications
 * @param {Function} params.t - Translation function
 * @param {Function} params.syncFunction - Sync function to call (syncOberonAll or syncOberonDelta)
 */
export const handleOberonImport = async ({
  customer,
  setImportOpen,
  setImportProgress,
  setOberonSyncJobId,
  setOberonSyncMessage,
  setOberonSyncStartTime,
  setIsOberonSyncRunning,
  enqueueSnackbar,
  t,
  syncFunction = syncOberonAll,
}) => {
  const customerCode = customer?.dsokSettings?.customer?.code;

  if (!customerCode) {
    enqueueSnackbar(t("pages.dsokCustomers.notifications.importFailed"), {
      variant: "error",
    });
    return;
  }

  // Close the modal immediately, we'll show inline status instead
  setImportOpen(false);
  setImportProgress(null);

  try {
    const { jobId } = await startOberonSync(customerCode, syncFunction);

    if (jobId) {
      // Save jobId and start time to localStorage for recovery after route change
      const startTime = Date.now();
      localStorage.setItem("oberonSyncJobId", jobId);
      localStorage.setItem("oberonSyncStartTime", startTime.toString());

      // Start polling mode
      setOberonSyncJobId(jobId);
      setOberonSyncMessage("Prebieha...");
      setOberonSyncStartTime(startTime);
      setIsOberonSyncRunning(true);
    } else {
      enqueueSnackbar(t("pages.dsokCustomers.notifications.importFailed"), {
        variant: "error",
      });
    }
  } catch (e) {
    enqueueSnackbar(
      e?.response?.data?.message ||
        t("pages.dsokCustomers.notifications.importFailed"),
      {
        variant: "error",
      },
    );
  }
};

/**
 * Legacy handler for backwards compatibility (full import)
 */
export const handleImportOberon = async (params) => {
  return handleOberonImport({
    ...params,
    syncFunction: syncOberonAll,
  });
};
